import { ReactComponent as ChevronDownIcon } from 'assett/icons/arrow_down_icon.svg';
import { ReactComponent as ChevronUpIcon } from 'assett/icons/arrow_up_icon.svg';
import { resetBillingPage } from 'feature/billing/billingSlice';
import { changePage } from 'feature/claims/claimsSlice';
import { handleChange, setSortingValues } from 'feature/form/formSlice';
import { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useBillingSubTab } from 'hooks/billingSubTab.hook';
import { AppDispatch, RootState } from 'store/store';
import { Box, Stack, Typography } from 'styles';
import TextField from 'styles/textfield/TextFieldLogin';
import { IFilterState, sortingDirections } from 'types/Form';
import { TRANSLATION_KEYS } from 'utils/constants';
import { useTranslation } from 'utils/translations';
import { camelcase } from 'utils/utils';

interface Props {
	name: string;
	children?: React.ReactNode;
	widthBox: number;
	widthInput?: number;
	primarySorting?: string;
	overrideLabel?: string;
	// field sent to the BE for sorting, when different from `name`
	sortField?: string;
	// column without a filter: always show the sortable label, even in search mode
	sortOnly?: boolean;
}

const HeaderCellWithSorting: FC<Props> = props => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});
	const dispatch = useDispatch<AppDispatch>();
	const inputForm = useSelector((store: RootState) => store.form);
	const currentInputName = `input${camelcase(props.name)}`;
	const sortField = props.sortField ?? props.name;
	const { view } = useBillingSubTab();

	const handleSorting = (name: string, value: string) => {
		dispatch(changePage(1));
		dispatch(resetBillingPage(view));

		if (props.primarySorting) {
			dispatch(
				setSortingValues({
					name: name + `,${props.primarySorting}`,
					value: `${value},${value}`,
				}),
			);
		} else {
			dispatch(setSortingValues({ name, value }));
		}
	};

	const handleOnChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
		dispatch(handleChange({ name: evt.target.name, value: evt.target.value }));
	};

	return (
		<Box sx={{ width: props.widthBox }}>
			{inputForm.search && !props.sortOnly ? (
				!props.children ? (
					<TextField
						label={
							props.overrideLabel
								? tClientLabels(props.overrideLabel)
								: tClientLabels(`forms.header.${props.name}`)
						}
						sx={{ width: props.widthInput }}
						name={`input${camelcase(props.name)}`}
						onChange={handleOnChange}
						value={inputForm[currentInputName as keyof IFilterState] || ''}
					/>
				) : (
					props.children
				)
			) : (
				<Stack direction={'row'} alignItems={'center'}>
					<Typography
						variant="bodyLink"
						sx={{ cursor: 'pointer' }}
						onClick={() =>
							handleSorting(
								sortField,
								inputForm.sortingFields.split(',')[0] === sortField &&
									inputForm.sortingDirections.split(',')[0] === sortingDirections.ASC
									? sortingDirections.DESC
									: sortingDirections.ASC,
							)
						}
					>
						{props.overrideLabel
							? tClientLabels(props.overrideLabel)
							: tClientLabels(`forms.header.${props.name}`)}
					</Typography>

					{inputForm.sortingFields.split(',')[0] === sortField ? (
						inputForm.sortingDirections.split(',')[0] === sortingDirections.ASC ? (
							<ChevronUpIcon />
						) : (
							<ChevronDownIcon />
						)
					) : null}
				</Stack>
			)}
		</Box>
	);
};

export default HeaderCellWithSorting;
